import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Card } from '@/components/Card';
import { post } from '@/lib/api';

export function EmployeeCreatePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    email: '',
    personal_email: '',
    phone: '',
    date_of_birth: '',
    employment_type: 'full_time' as string,
    department_name: '',
    title: '',
    manager_name: '',
    hire_date: '',
    start_date: '',
    location: '',
    remote: false,
    skills: '' as string,
    notes: '',
  });

  function update(field: string, value: string | boolean) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.hire_date) {
      setError('First name, last name, email, and hire date are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        hire_date: form.hire_date,
        employment_type: form.employment_type,
        title: form.title || undefined,
        department_name: form.department_name || undefined,
        manager_name: form.manager_name || undefined,
        start_date: form.start_date || form.hire_date,
        location: form.location || undefined,
        remote: form.remote,
        preferred_name: form.preferred_name || undefined,
        personal_email: form.personal_email || undefined,
        phone: form.phone || undefined,
        date_of_birth: form.date_of_birth || undefined,
        notes: form.notes || undefined,
        skills: form.skills ? form.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      const result = await post<{ id: string }>('employees', payload);
      navigate(`/employees/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create employee');
    } finally {
      setSaving(false);
    }
  }

  const inputClass = 'w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500';
  const labelClass = 'text-xs font-medium text-muted-foreground block mb-1';

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate('/employees')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to Employees
      </button>

      <h2 className="text-xl font-bold text-foreground">Add New Employee</h2>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Info */}
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>First Name *</label>
              <input value={form.first_name} onChange={e => update('first_name', e.target.value)} placeholder="John" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Last Name *</label>
              <input value={form.last_name} onChange={e => update('last_name', e.target.value)} placeholder="Smith" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Preferred Name</label>
              <input value={form.preferred_name} onChange={e => update('preferred_name', e.target.value)} placeholder="Johnny" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Date of Birth</label>
              <input type="date" value={form.date_of_birth} onChange={e => update('date_of_birth', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Work Email *</label>
              <input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="john@company.com" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Personal Email</label>
              <input type="email" value={form.personal_email} onChange={e => update('personal_email', e.target.value)} placeholder="john@gmail.com" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 555-0123" className={inputClass} />
            </div>
          </div>
        </Card>

        {/* Employment Info */}
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Employment Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Job Title</label>
              <input value={form.title} onChange={e => update('title', e.target.value)} placeholder="Help Desk Technician" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Department</label>
              <input value={form.department_name} onChange={e => update('department_name', e.target.value)} placeholder="Service Desk" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Employment Type</label>
              <select value={form.employment_type} onChange={e => update('employment_type', e.target.value)} className={inputClass}>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="intern">Intern</option>
                <option value="temp">Temp</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Manager</label>
              <input value={form.manager_name} onChange={e => update('manager_name', e.target.value)} placeholder="Jane Doe" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Hire Date *</label>
              <input type="date" value={form.hire_date} onChange={e => update('hire_date', e.target.value)} className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Start Date</label>
              <input type="date" value={form.start_date} onChange={e => update('start_date', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Location</label>
              <input value={form.location} onChange={e => update('location', e.target.value)} placeholder="New York, NY" className={inputClass} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input type="checkbox" id="remote" checked={form.remote} onChange={e => update('remote', e.target.checked)} className="rounded border-border" />
              <label htmlFor="remote" className="text-sm text-foreground">Remote Employee</label>
            </div>
          </div>
        </Card>

        {/* Additional Info */}
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Additional Details</h3>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Skills (comma-separated)</label>
              <input value={form.skills} onChange={e => update('skills', e.target.value)} placeholder="Active Directory, Networking, M365, Azure" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Notes</label>
              <textarea value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Any additional notes about this employee..." rows={3} className={inputClass} />
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate('/employees')} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-2">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> {saving ? 'Creating...' : 'Create Employee'}
          </button>
        </div>
      </form>
    </div>
  );
}
